import { useEffect, useRef, useState } from 'react';
import { X, Send, MessageCircle } from 'lucide-react';
import {
  creativeWorkbenchApi,
  getApiBase,
  type ChartOutputEvent,
  type WorkbenchChartSpec,
} from '@/services/api';
import { useCreativeWorkbench } from '../CreativeWorkbenchContext';
import { ChartBubble } from '@/components/chat/ChartBubble';
import { readChatLlmProviderCookie } from '@/lib/chatLlmProviderCookie';

export interface ChatSeedInput {
  question: string;
  persona_id?: string;
  playbook_id?: string;
  channel?: string;
  chart_axis?: Record<string, unknown>;
  date_range?: { from: string; to: string };
}

interface Props {
  seed: ChatSeedInput;
  onClose: () => void;
}

type Message =
  | { id: string; role: 'user'; type: 'text'; content: string }
  | { id: string; role: 'assistant'; type: 'text'; content: string }
  | { id: string; role: 'assistant'; type: 'chart'; chart: WorkbenchChartSpec; narrative?: string };

const URL_THREAD_PARAM = 'analyst_thread';

export function EmbeddedChatPanel({ seed, onClose }: Props) {
  const { clientUid, personaId, dateRange, analystThreadId, setAnalystThreadId } =
    useCreativeWorkbench();
  const [threadId, setThreadIdState] = useState<string | null>(analystThreadId ?? null);
  const [prefill, setPrefill] = useState(seed.question);
  const [messages, setMessages] = useState<Message[]>([]);
  const [contextBlocks, setContextBlocks] = useState<{ type: string; value: unknown }[]>([]);
  const [busy, setBusy] = useState(false);
  const seedAppliedRef = useRef(false);

  const setThreadId = (id: string | null) => {
    setThreadIdState(id);
    setAnalystThreadId?.(id);
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      if (id) url.searchParams.set(URL_THREAD_PARAM, id);
      else url.searchParams.delete(URL_THREAD_PARAM);
      window.history.replaceState(null, '', url.toString());
    }
  };

  // Restore thread history on mount when an analyst_thread is in the URL.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const existing = params.get(URL_THREAD_PARAM);
    if (existing && !threadId) {
      setThreadIdState(existing);
      setAnalystThreadId?.(existing);
      // Best-effort restoration of prior text turns; chart turns are not yet
      // persisted in the checkpointer (Phase 4 follow-up).
      void getThreadHistory(existing).then((history) => {
        if (history.length) setMessages(history);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Seed the workbench-side context once the panel opens.
  useEffect(() => {
    if (seedAppliedRef.current) return;
    seedAppliedRef.current = true;

    let cancelled = false;
    setBusy(true);
    creativeWorkbenchApi
      .seedChat({
        client_uid: clientUid ?? '',
        persona_id: seed.persona_id ?? personaId,
        playbook_id: seed.playbook_id,
        channel: seed.channel,
        chart_axis: seed.chart_axis,
        date_range: seed.date_range ?? dateRange,
        question: seed.question,
      })
      .then((r) => {
        if (cancelled) return;
        // Only adopt the seeded thread_id if we don't already have one
        // (URL-restored or server-assigned from a previous turn).
        if (!threadId) setThreadId(r.thread_id);
        setPrefill(r.prefill);
        setContextBlocks(r.context_blocks ?? []);
      })
      .catch(() => {
        if (!cancelled) setThreadIdState(null);
      })
      .finally(() => {
        if (!cancelled) setBusy(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seed]);

  const send = async () => {
    if (!prefill.trim() || busy) return;
    const question = prefill;
    const userId = makeId();
    const assistantId = makeId();
    setMessages((prev) => [
      ...prev,
      { id: userId, role: 'user', type: 'text', content: question },
      { id: assistantId, role: 'assistant', type: 'text', content: '' },
    ]);
    setPrefill('');
    setBusy(true);

    let resp: Response | null = null;
    try {
      // The chat backend allocates the LangGraph thread_id and returns it on
      // `done`. Until then we use whatever thread we have (URL-restored,
      // seeded by the workbench, or freshly minted client-side).
      const effectiveThreadId = threadId ?? `thr_workbench_${makeId()}`;
      if (!threadId) setThreadId(effectiveThreadId);

      const llmProvider = readChatLlmProviderCookie();

      resp = await fetch(`${getApiBase()}/api/v1/chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          thread_id: effectiveThreadId,
          message: { role: 'user', content: question },
          ...(llmProvider ? { provider: llmProvider } : {}),
          metadata: {
            source: 'embedded_analyst',
            client_uid: clientUid,
            persona_id: seed.persona_id ?? personaId,
            context_blocks: contextBlocks,
          },
        }),
      });
    } catch (err) {
      appendAssistantText(setMessages, assistantId, `\n\n⚠️ Network error: ${String(err)}`);
      setBusy(false);
      return;
    }

    if (!resp.ok || !resp.body) {
      const errText = await resp.text().catch(() => resp.statusText);
      appendAssistantText(setMessages, assistantId, `\n\n⚠️ ${resp.status}: ${errText.slice(0, 300)}`);
      setBusy(false);
      return;
    }

    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      const blocks = buf.split('\n\n');
      buf = blocks.pop() ?? '';
      for (const block of blocks) {
        const events = parseSseChunk(block + '\n\n');
        for (const { event, data } of events) {
          handleEvent(event, data, setMessages, assistantId, setBusy, setThreadId);
        }
      }
    }
    setBusy(false);
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden sticky top-20 max-h-[calc(100vh-6rem)] flex flex-col">
      <header className="flex items-center justify-between px-3 py-2 border-b border-slate-100 bg-slate-50">
        <div className="flex items-center gap-2">
          <MessageCircle size={14} className="text-teal-500" />
          <span className="text-xs font-bold text-navy-900">Analyst</span>
          {threadId && (
            <span className="text-[10px] font-mono text-slate-400 truncate max-w-[140px]">
              {threadId}
            </span>
          )}
        </div>
        <button
          onClick={onClose}
          className="text-slate-400 hover:text-slate-700"
          aria-label="Close"
        >
          <X size={14} />
        </button>
      </header>
      <div className="flex flex-wrap gap-1 px-3 py-2 border-b border-slate-100 bg-white">
        {contextBlocks.map((block, i) => (
          <span
            key={i}
            className="inline-flex items-center gap-1 bg-teal-50 text-teal-700 border border-teal-200 rounded-full px-1.5 py-0.5 text-[10px] font-mono"
          >
            {block.type}
          </span>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {messages.length === 0 && !busy && (
          <div className="text-[11px] text-slate-400">
            Ask a question — the context blocks above are already attached.
          </div>
        )}
        {busy && messages.length === 0 && (
          <div className="text-[11px] text-slate-400">Bootstrapping thread…</div>
        )}
        {messages.map((msg) => {
          if (msg.type === 'chart') {
            return (
              <ChartBubble
                key={msg.id}
                spec={msg.chart}
                narrative={msg.narrative}
                compact
                threadId={threadId ?? undefined}
              />
            );
          }
          return (
            <div
              key={msg.id}
              className={
                msg.role === 'user'
                  ? 'bg-teal-50 text-teal-900 rounded-lg px-3 py-2 text-xs whitespace-pre-wrap'
                  : 'bg-slate-50 text-slate-700 rounded-lg px-3 py-2 text-xs whitespace-pre-wrap'
              }
            >
              {msg.content || (msg.role === 'assistant' && busy ? '…' : '')}
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-2 p-2 border-t border-slate-100">
        <input
          value={prefill}
          onChange={(e) => setPrefill(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && send()}
          placeholder="Follow up..."
          className="flex-1 px-2 py-1.5 text-xs border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-teal-500/40"
        />
        <button
          onClick={send}
          disabled={!prefill.trim() || busy}
          className="p-1.5 text-teal-500 disabled:opacity-40 hover:text-teal-600"
        >
          <Send size={14} />
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function makeId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2);
}

function appendAssistantText(
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>,
  assistantId: string,
  delta: string,
) {
  setMessages((prev) =>
    prev.map((m) =>
      m.id === assistantId && m.role === 'assistant' && m.type === 'text'
        ? { ...m, content: m.content + delta }
        : m,
    ),
  );
}

function handleEvent(
  event: string,
  data: unknown,
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>,
  assistantId: string,
  setBusy: React.Dispatch<React.SetStateAction<boolean>>,
  setThreadId: (id: string | null) => void,
) {
  const d = data as Record<string, unknown>;
  if (event === 'message_delta') {
    appendAssistantText(setMessages, assistantId, (d.delta as string) ?? '');
  } else if (event === 'chart_output') {
    const ev = d as unknown as ChartOutputEvent;
    setMessages((prev) => [
      ...prev,
      {
        id: makeId(),
        role: 'assistant',
        type: 'chart',
        chart: ev.chart,
        narrative: ev.narrative,
      },
    ]);
  } else if (event === 'done') {
    const tid = (d.thread_id as string | undefined) ?? null;
    if (tid) setThreadId(tid);
    setBusy(false);
  } else if (event === 'error') {
    appendAssistantText(setMessages, assistantId, `\n\n⚠️ ${(d.message as string) ?? 'Unknown error'}`);
  }
}

function parseSseChunk(buf: string): { event: string; data: unknown }[] {
  const out: { event: string; data: unknown }[] = [];
  for (const block of buf.split('\n\n')) {
    if (!block.trim()) continue;
    let event = '';
    let dataStr = '';
    for (const line of block.split('\n')) {
      if (line.startsWith('event: ')) event = line.slice('event: '.length).trim();
      else if (line.startsWith('data: ')) dataStr += line.slice('data: '.length);
    }
    if (!event && !dataStr) continue;
    let data: unknown = dataStr;
    try {
      data = dataStr ? JSON.parse(dataStr) : null;
    } catch {
      // leave as raw string
    }
    out.push({ event, data });
  }
  return out;
}

async function getThreadHistory(threadId: string): Promise<Message[]> {
  try {
    const resp = await fetch(`${getApiBase()}/api/v1/chat/threads/${encodeURIComponent(threadId)}`);
    if (!resp.ok) return [];
    const json = (await resp.json()) as {
      messages?: { role: string; content: string }[];
    };
    return (json.messages ?? []).map((m) => ({
      id: makeId(),
      role: (m.role === 'user' ? 'user' : 'assistant') as 'user' | 'assistant',
      type: 'text',
      content: m.content,
    })) as Message[];
  } catch {
    return [];
  }
}
